// ============================================
// Blind Run Service
//
// Sends ONLY extracted lab biomarkers to Claude
// using the shared clinicalAnalysisPrompt.
// Does NOT see SOAP note — that's the "blind" part.
//
// Returns structured BlindRunOutput with clinical
// taxonomy (PrimaryFinding[]).
// ============================================

import { claudeGenerate } from '../ai/providers/claude';
import { SKILL_CONFIGS } from '../ai/index';
import { getActiveClinicalPrompt } from '../firestore/clinicalPromptService';
import type {
  ExtractedBiomarker,
  BlindRunOutput,
  PrimaryFinding,
  CaseDemographics,
} from '../../types/labUpload';

const BLIND_RUN_USER_TEMPLATE = `Analyze the following lab biomarker results for a patient. Provide a clinical assessment as a JSON object:

{
  "diagnosis": "Primary clinical impression based on lab patterns (string)",
  "protocol": "Recommended supplement/treatment protocol (string)",
  "recommendedProducts": ["Array of specific supplement/product recommendations"],
  "rootCauses": [
    {
      "rootCause": "One of: adrenal, thyroid, hormonal, metabolic, inflammatory, nutritional, hepatic, lifestyle, multi_system, unclassified",
      "confidence": 0.85,
      "supportingMarkers": ["biomarker IDs that support this finding"],
      "clinicalReasoning": "Brief clinical reasoning for this finding"
    }
  ],
  "followUpTimeline": "Recommended retest timeline (string)",
  "contraindications": ["Any contraindications or warnings"]
}

Patient Demographics: {{DEMOGRAPHICS}}

Lab Results:
{{BIOMARKERS}}

Rules:
- Identify root causes using the clinical taxonomy
- Multiple root causes are expected — most patients have 2-4
- Use functional/longevity-optimized ranges, not just conventional
- Flag patterns across biomarkers (e.g. thyroid + iron + cortisol = adrenal axis)
- Return ONLY valid JSON`;

/**
 * Run a blind clinical analysis on extracted biomarkers.
 * Claude sees ONLY the lab data — no SOAP, no Dr. C assessment.
 */
export async function runBlindAnalysis(
  biomarkers: ExtractedBiomarker[],
  demographics?: CaseDemographics,
): Promise<BlindRunOutput> {
  const config = SKILL_CONFIGS['lab-analyst'];

  // Load the active clinical prompt from Firestore
  const clinicalPrompt = await getActiveClinicalPrompt();
  const systemPrompt = clinicalPrompt?.systemPrompt ?? 'You are a clinical lab analyst specializing in functional and longevity medicine.';
  const promptVersionId = clinicalPrompt?.versionId ?? 'fallback';

  // Build biomarker text
  const biomarkerText = biomarkers
    .filter((b) => b.value !== null)
    .map((b) => {
      const range = b.standardRange
        ? ` (ref: ${b.standardRange.low}-${b.standardRange.high})`
        : '';
      return `- ${b.rawName}${b.canonicalId ? ` [${b.canonicalId}]` : ''}: ${b.value} ${b.unit}${range}`;
    })
    .join('\n');

  const demographicText = demographics
    ? `Age: ${demographics.age || 'unknown'}, Sex: ${demographics.sex || 'unknown'}`
    : 'Not provided';

  const userMessage = BLIND_RUN_USER_TEMPLATE
    .replace('{{DEMOGRAPHICS}}', demographicText)
    .replace('{{BIOMARKERS}}', biomarkerText);

  const response = await claudeGenerate(
    config.model,
    systemPrompt,
    userMessage,
    0,
    4096,
  );

  // Parse response
  let cleaned = response.trim();
  if (cleaned.startsWith('```')) {
    cleaned = cleaned.replace(/^```(?:json)?\n?/, '').replace(/\n?```$/, '');
  }

  const parsed = JSON.parse(cleaned) as {
    diagnosis: string;
    protocol: string;
    recommendedProducts: string[];
    rootCauses: PrimaryFinding[];
    followUpTimeline?: string;
    contraindications?: string[];
  };

  return {
    diagnosis: parsed.diagnosis || '',
    protocol: parsed.protocol || '',
    recommendedProducts: parsed.recommendedProducts || [],
    rootCauses: (parsed.rootCauses || []).map((rc) => ({
      rootCause: rc.rootCause || 'unclassified',
      confidence: Number(rc.confidence) || 0.5,
      supportingMarkers: rc.supportingMarkers || [],
      clinicalReasoning: rc.clinicalReasoning || '',
    })),
    followUpTimeline: parsed.followUpTimeline,
    contraindications: parsed.contraindications,
    rawResponse: response,
    promptVersionId,
    modelUsed: config.model,
    completedAt: new Date().toISOString(),
  };
}
